import { type BannerTranslations, type Banner } from '@prisma/client'
import { type ReactNode, useEffect } from 'react'
import { useSessionStorage } from '#app/hooks/use-session-storage.js'
import { LocalizedLink } from '#app/utils/i18n.js'
import Dot from '../ui/dot'
import { Icon } from '../ui/icon'

export default function Banner({
	id,
	title,
	description,
	link,
}: Partial<
	Pick<Banner, 'id'> &
		Pick<BannerTranslations, 'title' | 'description' | 'link'>
>) {
	const [open, setOpen] = useSessionStorage(`banner-${id}`, true)

	useEffect(() => {
		if (!open)
			// scroll to top
			window.scrollTo(0, 0)
	}, [open])

	if (!title || !open) return null

	const LinkIfNeeded = ({ children }: { children: ReactNode }) => {
		if (link)
			return (
				<LocalizedLink className="group w-full" to={link}>
					{children}
				</LocalizedLink>
			)

		return children
	}

	return (
		<div className="relative z-[150] row-start-1 flex items-center justify-between gap-x-6 border-b border-brand-dusk bg-white px-6 py-2.5 sm:pr-3.5 lg:pl-8">
			<p className="flex-1 self-center text-sm leading-6 text-brand-dusk">
				<LinkIfNeeded>
					<strong className="font-semibold">{title}</strong>
					{title && description && <Dot />}
					{description && <span>{description}</span>}
					{link && (
						<span aria-hidden="true">
							&nbsp;
							<Icon
								name="arrow-right"
								className="transition-all duration-200 ease-in-out group-hover:ml-xxs"
							/>
						</span>
					)}
				</LinkIfNeeded>
			</p>
			<button
				onClick={() => {
					setOpen(false)
				}}
				type="button"
				className="absolute right-sm top-1/2 flex-none -translate-y-1/2 focus-visible:outline-offset-[-4px]"
			>
				<span className="sr-only">Dismiss</span>
				<Icon
					name="lucide-x"
					aria-hidden="true"
					className="h-5 w-5 text-brand-dusk"
				/>
			</button>
		</div>
	)
}
